<template>
    <div class="modal upgrade-pro-modal">
        <div
            class="modal-content"
            :class="{'upgrade-modal-content-success': isSubscriptionUpdated }"
        >
            <base-modal-close @close-modal="closeModal" />

            <transition
                name="fade"
                mode="out-in"
            >
                <div
                    v-if="! isSubscriptionUpdated"
                    key="billing-change-plan"
                >
                    <h2>Change Your Billing Plan</h2>

                    <div class="upgrade-container flex">
                        <div class="plan-details">
                            <p class="section-title">
                                Your Current Plan
                            </p>
                            <billing-intervals
                                :intervals="intervals"
                                :value="selectedInterval"
                                :disabled="formProcessing"
                                @change="changeInterval"
                            />
                            <update-card
                                update-text="Update"
                                @card-form-switch="cardEditing = ! cardEditing"
                            />
                        </div>
                        <div class="payment-details">
                            <p class="section-title">
                                Payment Overview
                            </p>

                            <transition
                                name="fade"
                                mode="out-in"
                            >
                                <base-loader
                                    v-if="! userCard || formProcessing || (isChangedInterval && ! presumedInvoice)"
                                />

                                <div v-else>
                                    <div class="upgrade-items">
                                        <div
                                            v-for="(item, key) in billingSummary"
                                            :key="key"
                                            class="overview-line upgrade-line item-line"
                                        >
                                            <p class="upgrade-label">
                                                # of {{ item.name }}:
                                            </p>
                                            <div class="line-site-price site-price">
                                                {{ item.quantity }}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        class="overview-line upgrade-line old-site-price"
                                    >
                                        <p class="upgrade-label">
                                            Your current {{ toLower(userIntervalName) }} payment
                                        </p>
                                        <div
                                            class="input"
                                        >
                                            <span class="site-price">
                                                {{ usdFormatter(currentTotalAmount) }}
                                            </span>
                                        </div>
                                    </div>

                                    <div
                                        v-if="isChangedInterval"
                                        class="overview-line upgrade-line"
                                    >
                                        <p class="upgrade-label">
                                            Your new {{ toLower(selectedIntervalName) }} payment
                                        </p>
                                        <div
                                            class="input"
                                        >
                                            <span class="site-price">
                                                {{ usdFormatter(newTotalAmount) }}
                                            </span>
                                        </div>
                                    </div>

                                    <div
                                        v-if="isChangedInterval"
                                        class="plan-info-box"
                                    >
                                        <p>
                                            <span>
                                                You’re switching from {{ userIntervalName }} to {{ selectedIntervalName }} Plan.
                                            </span>
                                        </p>
                                        <p>You’ll be charged <span>{{ usdFormatter(presumedInvoice.amount_due) }}</span> <span>{{ date( presumedInvoice.next_payment_attempt ) }}</span> and then <span>{{ usdFormatter(newTotalAmount) }}</span> each {{ selectedInterval }}.</p>
                                    </div>

                                    <div
                                        v-if="! userCard && ! formProcessing"
                                        class="card-error-box"
                                    >
                                        <p>
                                            Please insert Credit Card details to update your subscription
                                        </p>
                                    </div>
                                </div>
                            </transition>

                            <base-submit-button
                                :disabled="disabled || formProcessing || ! userCard || cardEditing"
                                class="btn"
                                :loading="isSubmitting"
                                @click="updateSubscription"
                            >
                                Save Changes to Your Plan
                            </base-submit-button>
                            <div class="cancel-container">
                                <a href="#" @click.prevent="closeModal">
                                    Cancel changes
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-else
                    key="success"
                    class="upgrade-success"
                >
                    <svg-vue class="upgrade-success-image" icon="register-success" />
                    <h2>Success!</h2>
                    <p>Changes to your Fusebox Pro plan has been saved.</p>
                    <base-submit-button
                        class="btn"
                        handle-enter="true"
                        @click="closeModal"
                    >
                        Close
                    </base-submit-button>
                </div>
            </transition>
        </div>
    </div>
</template>
<script>
import { reduce, toLower } from 'lodash';
import moment from 'moment';
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex';
import BillingIntervals from '@/components/Billing/BillingIntervals';
import UpdateCard from '@/components/Billing/UpdateCard';
import apiCall from '@/utils/apiCall';
import usdFormatter from '@/utils/usdFormatter';
import modal from '@/mixins/modal';

export default {
    components: {
        UpdateCard,
        BillingIntervals,
    },

    mixins: [modal],

    props: {
        isSubscriptionUpdated: {
            type: Boolean,
            required: true,
        },
        isSubmitting: {
            type: Boolean,
            required: true,
        },
    },

    data: () => ({
        intervals: {
            month: 'Monthly',
            year: 'Yearly',
        },
        disabled: true,
        formProcessing: false,
        selectedInterval: null,
        summary: false,
        cardEditing: false,
        summaries: {},
    }),

    computed: {
        ...mapState([
            'billingSummary',
            'userSubscription',
            'presumedInvoice',
        ]),

        ...mapGetters([
            'userCard',
        ]),

        /**
         * Returns the name of selected interval.
         *
         * @returns {string} Name of selected interval.
         */
        selectedIntervalName() {
            return this.intervals[this.selectedInterval];
        },

        /**
         * Returns user current interval.
         *
         * @returns {string} User current interval.
         */
        userInterval() {
            return this.userSubscription?.interval;
        },

        /**
         * Returns the name of user interval.
         *
         * @returns {string} Name of user interval.
         */
        userIntervalName() {
            return this.intervals[this.userInterval];
        },

        /**
         * Indicates whether interval is changed or not.
         *
         * @returns {boolean} Is interval changed or not?
         */
        isChangedInterval() {
            return this.selectedInterval !== this.userInterval;
        },

        /**
         * Returns total amount of current subscription.
         *
         * @returns {number} Total amount of current subscription.
         */
        currentTotalAmount() {
            return reduce(this.userSubscription.items.data, (sum, item) => {
                return sum + (item.price.unit_amount * item.quantity);
            }, 0);
        },

        /**
         * Returns total amount of changed interval subscription.
         *
         * @returns {number} Total amount of changed interval subscription.
         */
        newTotalAmount() {
            return reduce(this.summary, (sum, item) => {
                return sum + (item.amount * item.quantity);
            }, 0);
        },
    },

    /**
     * Loads necessary data.
     */
    async mounted() {
        this.selectedInterval = this.userInterval ?? null;
        this.summary = this.billingSummary ?? false;
    },

    methods: {
        ...mapActions(['loadUpcomingInvoice']),
        ...mapMutations(['changeUIState']),

        usdFormatter,
        toLower,

        /**
         * Loads summary data for selected interval.
         *
         * @param  {string} interval Selected interval.
         */
        async changeInterval(interval) {
            this.selectedInterval = interval;

            if (! this.isChangedInterval) {
                this.summary = this.billingSummary;

                return;
            }

            if (! this.summaries[interval]) {
                this.disabled = true;
                this.formProcessing = true;

                const [{ data }] = await Promise.all([
                    this.loadSummary(interval),
                    this.loadUpcomingInvoice({ interval, presumed: true }),
                ]);

                this.summaries[interval] = data;

                this.disabled = false;
                this.formProcessing = false;
            }

            this.summary = this.summaries[interval];
        },

        /**
         * Closes upgrade modal
         */
        closeModal() {
            this.changeUIState({ isBillingPlanChangeVisible: false });
        },

        /**
         * Load summary of subscription for given interval.
         *
         * @param   {string} interval Subscription interval.
         * @returns {Promise}          Request promise.
         */
        loadSummary(interval) {
            const route = `/api/billing/subscriptions/${this.userSubscription.dbId}/summary`;

            return apiCall(this, 'get', route, { params: { interval } });
        },

        /**
         * Emit update subscription event
         */
        updateSubscription() {
            this.disabled = true;

            this.$emit(
                'update-subscription',
                this.userSubscription.dbId,
                {
                    interval: this.selectedInterval,
                    payment_method: this.userSubscription.default_payment_method,
                }
            );
        },

        /**
         * Formats date
         *
         * @param  {number} date Date timestamp
         * @returns {string}      Formated date
         */
        date(date) {
            const momentDate = moment.unix(date);

            return momentDate.isSame(new Date(), 'day') || null === date
                ? 'today' : momentDate.format('MMM D, YYYY');
        },
    },
};
</script>
